/**
 * @fileoverview
 * Scorecard rule configuration constants: operator input types and API mapping.
 * Operators grouped as Numeric Ops, String Ops, Boolean Ops, Date Ops (List ops excluded per requirement).
 *
 * @version 1.0.0
 * @author KATALYST TEAM
 */

export type OperatorInputType =
  | 'single'       // 1 input (value)
  | 'minMax'       // 2 inputs (Min, Max)
  | 'multi'        // multi-select or comma-separated
  | 'none'         // no value field
  | 'date'         // 1 date picker
  | 'betweenDates'; // 2 date pickers

/** Label -> symbol map (source of truth for display). List operators (In, Not In, Is In List, Not In List, Any) excluded. */
export const LABEL_TO_SYMBOL_MAP: Record<string, string> = {
  Equals: '=',
  'Not Equals': '!=',
  'Greater than': '>',
  'Less than': '<',
  'Greater than or equals': '>=',
  'Less than or equals': '<=',
  Exists: 'exists',
  'Not Exist': 'not_exists',
  'Is Null': 'is_null',
  'Not Null': 'not_null',
  Between: 'between',
  'Not Between': 'not_between',
  'Between Inclusive': 'between_inclusive',
  'Between Exclusive': 'between_exclusive',
  'Is Even': 'is_even',
  'Is Odd': 'is_odd',
  'Includes Text': 'includes_text',
  'Excludes Text': 'excludes_text',
  'Contains In Any Item': 'contains_in_any_item',
  'Contains In No Item': 'contains_in_no_item',
  'Regex Match': 'regex_match',
  'Starts With': 'starts_with',
  'Does Not Starts With': 'does_not_starts_with',
  'Ends With': 'ends_with',
  'Does Not Ends With': 'does_not_ends_with',
  'In Text': 'in_text',
  'Not In Text': 'not_in_text',
  'Regex Not Match': 'regex_not_match',
  'Includes All Words': 'includes_all_words',
  'Includes Any Words': 'includes_any_words',
  'Includes No Words': 'includes_no_words',
  Empty: 'empty',
  'Is Empty': 'is_empty',
  'Not Empty': 'not_empty',
  'Is True': 'is_true',
  'Is False': 'is_false',
  'Match All': 'match_all',
  'Not Match All': 'not_match_all',
  'All Items Match': 'all_items_match',
  'No Item Match': 'no_item_match',
  'Any Item Match': 'any_item_match',
  'Same Field Values': 'same_field_values',
  'Distinct Field Values': 'distinct_field_values',
  'Sum Items': 'sum_items',
  'Sum Product Items': 'sum_product_items',
  'Max Items': 'max_items',
  'Min Items': 'min_items',
  'Avg Items': 'avg_items',
  'Count Items': 'count_items',
  Any: 'any',
};

/** Operator groups for logic rule dropdown: Numeric, String, Boolean, Date time, List. */
export const OPERATOR_GROUPS: { label: string; options: { value: string; label: string }[] }[] = [
  {
    label: 'Numeric Ops',
    options: [
      { value: '=', label: 'Equals' },
      { value: '!=', label: 'Not Equals' },
      { value: '>', label: 'Greater than' },
      { value: '<', label: 'Less than' },
      { value: '>=', label: 'Greater than or equals' },
      { value: '<=', label: 'Less than or equals' },
      { value: 'between_inclusive', label: 'Between Inclusive' },
      { value: 'not_between', label: 'Not Between' },
      { value: 'between_exclusive', label: 'Between Exclusive' },
      { value: 'is_even', label: 'Is Even' },
      { value: 'is_odd', label: 'Is Odd' },
      { value: 'in', label: 'In' },
      { value: 'not_in', label: 'Not In' },
      { value: 'any', label: 'Any' },
    ],
  },
  {
    label: 'String Ops',
    options: [
      { value: '=', label: 'Equals' },
      { value: '!=', label: 'Not Equals' },
      { value: 'includes_text', label: 'Includes Text' },
      { value: 'excludes_text', label: 'Excludes Text' },
      { value: 'contains_in_any_item', label: 'Contains In Any Item' },
      { value: 'contains_in_no_item', label: 'Contains In No Item' },
      { value: 'regex_match', label: 'Regex Match' },
      { value: 'starts_with', label: 'Starts With' },
      { value: 'does_not_starts_with', label: 'Does Not Starts With' },
      { value: 'ends_with', label: 'Ends With' },
      { value: 'does_not_ends_with', label: 'Does Not Ends With' },
      { value: 'in_text', label: 'In Text' },
      { value: 'not_in_text', label: 'Not In Text' },
      { value: 'regex_not_match', label: 'Regex Not Match' },
      { value: 'is_in_list', label: 'Is In List' },
      { value: 'not_in_list', label: 'Not In List' },
      { value: 'includes_all_words', label: 'Includes All Words' },
      { value: 'includes_any_words', label: 'Includes Any Words' },
      { value: 'includes_no_words', label: 'Includes No Words' },
      { value: 'empty', label: 'Empty' },
      { value: 'not_empty', label: 'Not Empty' },
      { value: 'any', label: 'Any' },
    ],
  },
  {
    label: 'Boolean Ops',
    options: [
      { value: 'is_true', label: 'Is True' },
      { value: 'is_false', label: 'Is False' },
      { value: 'in', label: 'In' },
      { value: 'not_in', label: 'Not In' },
      { value: 'any', label: 'Any' },
    ],
  },
  {
    label: 'Date time Ops',
    options: [
      { value: '>', label: 'Greater Than' },
      { value: '<', label: 'Less Than' },
      { value: '>=', label: 'Greater Than or Equal' },
      { value: '<=', label: 'Less Than or Equal' },
      { value: 'between', label: 'Between' },
      { value: 'not_between', label: 'Not Between' },
      { value: 'between_inclusive', label: 'Between Inclusive' },
      { value: 'between_exclusive', label: 'Between Exclusive' },
      { value: 'any', label: 'Any' },
    ],
  },
  {
    label: 'List Ops',
    options: [
      { value: 'empty', label: 'Empty' },
      { value: 'not_empty', label: 'Not Empty' },
      { value: '=', label: 'Equals' },
      { value: '!=', label: 'Not Equals' },
      { value: 'in', label: 'In' },
      { value: 'not_in', label: 'Not In' },
      { value: 'includes_text', label: 'Includes Text' },
      { value: 'excludes_text', label: 'Excludes Text' },
      { value: 'match_all', label: 'Match All' },
      { value: 'not_match_all', label: 'Not Match All' },
      { value: 'all_items_match', label: 'All Items Match' },
      { value: 'no_item_match', label: 'No Item Match' },
      { value: 'any_item_match', label: 'Any Item Match' },
      { value: 'same_field_values', label: 'Same Field Values' },
      { value: 'distinct_field_values', label: 'Distinct Field Values' },
      { value: 'sum_items', label: 'Sum Items' },
      { value: 'sum_product_items', label: 'Sum Product Items' },
      { value: 'max_items', label: 'Max Items' },
      { value: 'min_items', label: 'Min Items' },
      { value: 'avg_items', label: 'Avg Items' },
      { value: 'count_items', label: 'Count Items' },
      { value: 'count_distinct_items', label: 'Count Distinct Items' },
      { value: 'any', label: 'Any' },
    ],
  },
];

/** Operator value -> input type for dynamic RANGE/VALUE column */
export const OPERATOR_INPUT_MAP: Record<string, OperatorInputType> = {
  '=': 'single',
  '!=': 'single',
  '>': 'single',
  '>=': 'single',
  '<': 'single',
  '<=': 'single',
  exists: 'none',
  not_exists: 'none',
  is_null: 'none',
  not_null: 'none',
  between: 'minMax',
  not_between: 'minMax',
  between_inclusive: 'minMax',
  between_exclusive: 'minMax',
  is_even: 'none',
  is_odd: 'none',
  sum_items: 'single',
  sum_product_items: 'single',
  max_items: 'single',
  min_items: 'single',
  avg_items: 'single',
  count_items: 'single',
  count_distinct_items: 'single',
  includes_text: 'single',
  excludes_text: 'single',
  contains_in_any_item: 'single',
  contains_in_no_item: 'single',
  regex_match: 'single',
  starts_with: 'single',
  does_not_starts_with: 'single',
  ends_with: 'single',
  does_not_ends_with: 'single',
  in: 'multi',
  not_in: 'multi',
  is_in_list: 'multi',
  not_in_list: 'multi',
  in_text: 'single',
  not_in_text: 'single',
  regex_not_match: 'single',
  includes_all_words: 'single',
  includes_any_words: 'single',
  includes_no_words: 'single',
  empty: 'none',
  is_empty: 'none',
  not_empty: 'none',
  is_true: 'none',
  is_false: 'none',
  any: 'none',
  match_all: 'none',
  not_match_all: 'none',
  all_items_match: 'none',
  no_item_match: 'none',
  any_item_match: 'none',
  same_field_values: 'none',
  distinct_field_values: 'none',
  before: 'date',
  after: 'date',
  betweenDates: 'betweenDates',
  // Legacy / backward compatibility
  contains: 'single',
  notContains: 'single',
  doesNotStartWith: 'single',
  doesNotEndWith: 'single',
  betweenInclusive: 'minMax',
  betweenExclusive: 'minMax',
  isNull: 'none',
  isNotNull: 'none',
  doesNotExist: 'none',
  notIn: 'multi',
};

/** Map UI operator value to API operator key and optional value (value = symbol; when no symbol, pass only key). */
export const OPERATOR_TO_API: Record<string, { key: string; value?: string }> = {
  '=': { key: 'Equals', value: '=' },
  '!=': { key: 'Not Equals', value: '!=' },
  '>': { key: 'Greater than', value: '>' },
  '>=': { key: 'Greater than or equals', value: '>=' },
  '<': { key: 'Less than', value: '<' },
  '<=': { key: 'Less than or equals', value: '<=' },
  exists: { key: 'Exists', value: 'exists' },
  not_exists: { key: 'Not Exist', value: 'not_exists' },
  is_null: { key: 'Is Null', value: 'is_null' },
  not_null: { key: 'Not Null', value: 'not_null' },
  between: { key: 'Between', value: 'between' },
  not_between: { key: 'Not Between', value: 'not_between' },
  between_inclusive: { key: 'Between Inclusive', value: 'between_inclusive' },
  between_exclusive: { key: 'Between Exclusive', value: 'between_exclusive' },
  is_even: { key: 'Is Even', value: 'is_even' },
  is_odd: { key: 'Is Odd', value: 'is_odd' },
  sum_items: { key: 'Sum Items', value: 'sum_items' },
  sum_product_items: { key: 'Sum Product Items', value: 'sum_product_items' },
  max_items: { key: 'Max Items', value: 'max_items' },
  min_items: { key: 'Min Items', value: 'min_items' },
  avg_items: { key: 'Avg Items', value: 'avg_items' },
  count_items: { key: 'Count Items', value: 'count_items' },
  count_distinct_items: { key: 'Count Distinct Items', value: 'count_distinct_items' },
  includes_text: { key: 'Includes Text', value: 'includes_text' },
  excludes_text: { key: 'Excludes Text', value: 'excludes_text' },
  contains_in_any_item: { key: 'Contains In Any Item', value: 'contains_in_any_item' },
  contains_in_no_item: { key: 'Contains In No Item', value: 'contains_in_no_item' },
  regex_match: { key: 'Regex Match', value: 'regex_match' },
  starts_with: { key: 'Starts With', value: 'starts_with' },
  does_not_starts_with: { key: 'Does Not Starts With', value: 'does_not_starts_with' },
  ends_with: { key: 'Ends With', value: 'ends_with' },
  does_not_ends_with: { key: 'Does Not Ends With', value: 'does_not_ends_with' },
  in: { key: 'In', value: 'in' },
  not_in: { key: 'Not In', value: 'not_in' },
  is_in_list: { key: 'Is In List', value: 'is_in_list' },
  not_in_list: { key: 'Not In List', value: 'not_in_list' },
  in_text: { key: 'In Text', value: 'in_text' },
  not_in_text: { key: 'Not In Text', value: 'not_in_text' },
  regex_not_match: { key: 'Regex Not Match', value: 'regex_not_match' },
  includes_all_words: { key: 'Includes All Words', value: 'includes_all_words' },
  includes_any_words: { key: 'Includes Any Words', value: 'includes_any_words' },
  includes_no_words: { key: 'Includes No Words', value: 'includes_no_words' },
  empty: { key: 'Empty', value: 'empty' },
  is_empty: { key: 'Is Empty', value: 'is_empty' },
  not_empty: { key: 'Not Empty', value: 'not_empty' },
  is_true: { key: 'Is True', value: 'is_true' },
  is_false: { key: 'Is False', value: 'is_false' },
  any: { key: 'Any', value: 'any' },
  match_all: { key: 'Match All', value: 'match_all' },
  not_match_all: { key: 'Not Match All', value: 'not_match_all' },
  all_items_match: { key: 'All Items Match', value: 'all_items_match' },
  no_item_match: { key: 'No Item Match', value: 'no_item_match' },
  any_item_match: { key: 'Any Item Match', value: 'any_item_match' },
  same_field_values: { key: 'Same Field Values', value: 'same_field_values' },
  distinct_field_values: { key: 'Distinct Field Values', value: 'distinct_field_values' },
  before: { key: 'Before', value: 'before' },
  after: { key: 'After', value: 'after' },
  betweenDates: { key: 'Between Dates', value: 'betweenDates' },
  // Legacy / backward compatibility
  contains: { key: 'Contains', value: 'contains' },
  notContains: { key: 'Not Contains', value: 'notContains' },
  doesNotStartWith: { key: 'Does Not Start With', value: 'doesNotStartWith' },
  doesNotEndWith: { key: 'Does Not End With', value: 'doesNotEndWith' },
  betweenInclusive: { key: 'Between (Inclusive)', value: 'betweenInclusive' },
  betweenExclusive: { key: 'Between (Exclusive)', value: 'betweenExclusive' },
  isNull: { key: 'Is Null', value: 'is_null' },
  isNotNull: { key: 'Is Not Null', value: 'not_null' },
  doesNotExist: { key: 'Does Not Exist', value: 'not_exists' },
  notIn: { key: 'Not In', value: 'notIn' },
};
